import React, { useState } from 'react';
import { computeStats, categoryBreakdown, fmtDay, fmtDuration, relativeDay } from '../myDayUtils';

/**
 * Fourteen days of follow-through.
 *
 * One series — activities completed — drawn against a recessive track showing
 * what was planned that day, so "8 of 10" and "8 of 20" never look alike. Every
 * bar carries a tooltip, today is direct-labelled, and the same numbers are one
 * button away as a table.
 */
export default function InsightsView({ tasks, cats, today, days }) {
  const [asTable, setAsTable] = useState(false);
  const stats = computeStats(tasks, today, 14, days);
  const rows = categoryBreakdown(tasks, cats);
  const peak = Math.max(1, ...stats.history.map(d => d.planned));

  const tiles = [
    { label: 'Perfect-day streak', value: stats.streak, unit: stats.streak === 1 ? 'day' : 'days', hint: 'Days where everything planned got done' },
    { label: 'Last 7 days', value: `${stats.rateLast7}%`, unit: `${stats.doneLast7}/${stats.plannedLast7}`, hint: 'Share of planned activities completed' },
    { label: 'Focus logged', value: fmtDuration(stats.focusMin), unit: 'total', hint: 'Time banked from focus sessions' },
    { label: 'Still carrying', value: stats.carriedOpen, unit: stats.carriedOpen === 1 ? 'activity' : 'activities', hint: 'Open items that have already rolled over at least once' },
    { label: 'Completed all time', value: stats.totalDone, unit: 'activities', hint: 'Everything ticked off since you started' },
  ];

  return (
    <div className="md-insights">
      <div className="md-tiles">
        {tiles.map(t => (
          <div key={t.label} className="md-tile" title={t.hint}>
            <span className="md-tile__lbl">{t.label}</span>
            <span className="md-tile__val">{t.value}</span>
            <span className="md-tile__unit">{t.unit}</span>
          </div>
        ))}
      </div>

      <section className="md-card">
        <header className="md-card__head">
          <div>
            <h3 className="md-card__title">Activities completed — last 14 days</h3>
            <p className="md-card__sub">Solid bar: completed. Track behind it: everything that day carried — including what rolled over.</p>
          </div>
          <button className="md-btn md-btn--sm" onClick={() => setAsTable(v => !v)}>
            {asTable ? '▮ Show chart' : '▤ Show table'}
          </button>
        </header>

        {asTable ? (
          <div className="md-tablewrap">
            <table className="md-table">
              <caption className="md-sr">Activities planned and completed per day over the last 14 days</caption>
              <thead><tr><th scope="col">Day</th><th scope="col">Completed</th><th scope="col">Planned</th></tr></thead>
              <tbody>
                {stats.history.map(d => (
                  <tr key={d.date}>
                    <th scope="row">{fmtDay(d.date)}</th>
                    <td>{d.done}</td>
                    <td>{d.planned}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="md-chart" role="img"
            aria-label={`Bar chart of activities completed each day over the last 14 days. ${stats.history.map(d => `${fmtDay(d.date)}: ${d.done} of ${d.planned}`).join('. ')}`}>
            {stats.history.map(d => {
              const isToday = d.date === today;
              return (
                <div key={d.date} className={`md-chart__col${isToday ? ' md-chart__col--today' : ''}`}>
                  <span className="md-chart__tip" aria-hidden="true">
                    {fmtDay(d.date)}<b>{d.done} done</b>of {d.planned} planned
                  </span>
                  <span className="md-chart__track">
                    <span className="md-chart__plan" style={{ height: `${(d.planned / peak) * 100}%` }} />
                    <span className="md-chart__bar" style={{ height: `${(d.done / peak) * 100}%` }} />
                  </span>
                  {isToday && d.done > 0 && <span className="md-chart__label">{d.done}</span>}
                  <span className="md-chart__x">{fmtDay(d.date, { weekday: 'narrow' })}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="md-card__foot">Today is {relativeDay(today, today).toLowerCase()} — the bar on the right.</p>
      </section>

      <section className="md-card">
        <header className="md-card__head">
          <div>
            <h3 className="md-card__title">Where your days go</h3>
            <p className="md-card__sub">Completion rate by category, biggest workload first.</p>
          </div>
        </header>
        {rows.length === 0 ? (
          <p className="md-empty">Nothing logged yet.</p>
        ) : (
          <ul className="md-bars">
            {rows.map(r => (
              <li key={r.id} className="md-bars__row">
                <span className="md-bars__lbl">
                  <i className="md-bars__dot" style={{ background: r.meta.color }} aria-hidden="true" />
                  {r.meta.icon} {r.meta.name}
                </span>
                <span className="md-bars__track">
                  <span className="md-bars__fill" style={{ width: `${r.pct}%`, background: r.meta.color }} />
                </span>
                <span className="md-bars__val">{r.done}/{r.total} · {r.pct}%</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
